import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useToast } from "../../Hooks/useToast";
import imageCompression from "browser-image-compression";

import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import FormLabel from "@mui/material/FormLabel";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import Grid from "@mui/material/Grid";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";


import AlternativeHeader from "../../Components/Headres/Alternative_Header";
import FromikTextField from "../../Components/Utils/Formik_Texfiled";
import CustomButton from "../../Components/Utils/Custom_Btn";
import ExtraObjectsInput from "../../Components/Utils/ExtraObject_Formik";
import FormikAuto from "../../Components/Utils/Formik_Autocom";

import { FetchCreateReservations } from "../../Apis/Reservations/Reservations";
import { fetchUsersAutocomplete } from "../../Redux/Users_Slice";
import { fetchPackegesAutocomplete } from "../../Redux/Package_AutoComplete";
import { fetchGetAllExtraObjectApi } from "../../Redux/ExtraObjects_Slice";

const VisuallyHiddenInput = (props) => (
  <input style={{ display: "none" }} {...props} />
);

const CreateReservation = () => {
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [dateOfReservation, setDateOfReservation] = useState(new Date());
  const [isCompressing, setIsCompressing] = useState(false);
  const navigate = useNavigate();
  const { toastSuccess, toastError } = useToast();
  const dispatch = useDispatch();
  const data = useSelector((item) => item.Users || []);
  const object = useSelector((item) => item.ExtraObjects || []);
  const packagename = useSelector((item) => item.PackegesAutocomplete || []);

  useEffect(() => {
    dispatch(fetchGetAllExtraObjectApi());
    dispatch(fetchUsersAutocomplete({ page: 1, limit: Number.MAX_SAFE_INTEGER ||[] }));
    dispatch(fetchPackegesAutocomplete({ page: 1, limit: Number.MAX_SAFE_INTEGER  ||[]}));
  }, []);

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
        if (file) {
          try {
            const options = {
              maxSizeMB: 1,
              maxWidthOrHeight: 1024,
              useWebWorker: true,
            };
    
            const compressedFile = await imageCompression(file, options);
            setSelectedImage(compressedFile);
            setImagePreview(URL.createObjectURL(compressedFile));
          } catch (error) {
            toastError("Bildkomprimierung fehlgeschlagen.");
          }
        }
      };

  const formik = useFormik({
    initialValues: {
      packageName: "",
      email: "",
      extraObjectsList: [],
      numberOfPersons: 1,
      payWay: "code",
      note: "",
    },
    onSubmit: async (values) => {
      try {
        if (!selectedImage) {
          toastError("Bitte laden Sie ein Bild hoch.");
          return;
        }

        setLoading(true);
        const formData = new FormData();
        formData.append("email", values.email);
        formData.append("packageName", values.packageName);

        values.extraObjectsList.forEach((item, index) => {
          formData.append(`extraObjectsList[${index}][name]`, item.name || null);
          formData.append(`extraObjectsList[${index}][price]`, item.price || 0);
          formData.append(`extraObjectsList[${index}][number]`, item.number || 1);
        });

        formData.append("numberOfPersons", values.numberOfPersons);
        formData.append("payWay", values.payWay);
        formData.append("note", values.note);
        formData.append("image", selectedImage);

        if (dateOfReservation) {
          const isoStringWithoutTimezone = new Date(
            dateOfReservation.getTime() - (dateOfReservation.getTimezoneOffset() * 60000)
          ).toISOString();
          formData.append("dateOfReservation", isoStringWithoutTimezone);
        }

        const result = await FetchCreateReservations(formData);
        if (result?.status === 201) {
          toastSuccess("Die Reservierung wurde erfolgreich erstellt!");
          navigate("/");
        } else {
          const errorMsg = result?.data?.message || "Die Erstellung der Reservierung ist fehlgeschlagen.";
          toastError(errorMsg);
        }
      } catch (error) {
        const serverMessage = error?.response?.data?.message || error?.message || "Während der Übermittlung ist ein Fehler aufgetreten.";
        toastError(serverMessage);
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box ml={2} component="form" onSubmit={formik.handleSubmit}>
        <AlternativeHeader onPrevious={() => navigate("/")} title="Buchungsdetails" />
        <Box mt={6}>
          <Grid container spacing={4}>
            <Grid mt={0.2} size={4}>
              <FormLabel sx={{ fontStyle: "inherit", color: "#000000" }}>
                Reservierungsdatum
              </FormLabel>
              <DateTimePicker
                minutesStep={30}
                sx={{
                  width: "100%",
                  bgcolor: "white",
                  mt: 1,
                  borderRadius: "12px",
                  '& .MuiClock-root': { display: 'none' },
                  '& .MuiTypography-root': { display: 'none' }
                }}
                value={dateOfReservation}
                onChange={(newValue) => setDateOfReservation(newValue)}
                minDateTime={new Date()}
                ampm={false}
                format="HH:mm.dd.MM.yyyy "
                renderInput={(params) => (
                  <FromikTextField 
                  formik={formik} 
                  name="dateOfReservation" 
                  {...params} 
                  fullWidth 
                  />
                )}
              />
            </Grid>

            <Grid size={4}>
              <FormikAuto
                formik={formik}
                name="email"
                label="Email"
                options={data?.data?.data ? data.data.data.map(user => ({
                  label: user.email,
                  value: user.email
                })) : []}
              />
            </Grid>

            <Grid size={4}>
              <FormikAuto
                formik={formik}
                name="packageName"
                label="Package Name"
                options={"data" in packagename.data ? packagename.data.data.map(pkg => ({
                  label: pkg.name,
                  value: pkg.name
                })) : []}
              />
            </Grid>

            <Grid size={4}>
              <FromikTextField 
                formik={formik} 
                name="numberOfPersons" 
                label="Anzahl der Personen" 
                placeholder="z.B. 2" 
                type="number" 
              />
            </Grid>

            <Grid size={4}>
              <FromikTextField 
                formik={formik} 
                name="payWay" 
                label="Zahlungsmethode" 
                placeholder="z.B. Code" 
                select
              >
                <MenuItem value="code">Code</MenuItem>
                <MenuItem value="paypal">PayPal</MenuItem>
              </FromikTextField>
            </Grid>

            <Grid size={12}>
              <Typography variant="subtitle1" gutterBottom>
              Extras 
              buchung
              </Typography>
              <ExtraObjectsInput formik={formik} extraObjects={object?.data || []} />
            </Grid>

            <Grid size={12}>
              <Box
                sx={{
                  display: "flex",
                  width: { md: "200px" },
                  height: { xs: "300px", md: "200px" },
                  border: "2px solid #D6B981",
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "12px",
                }}
              >
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="preview"
                    style={{ width: "90%", height: "90%", objectFit: "cover" }}
                  />
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    Kein Bild hochgeladen
                  </Typography>
                )}
              </Box>
              <Button
                sx={{ ml: 1.8, mt: 3 }}
                component="label"
                variant="contained"
                style={{ backgroundColor: "#D6B981" }}
                startIcon={<CloudUploadIcon />}
                disabled={isCompressing}
              >
                {isCompressing ? "Komprimierung läuft..." : "Bild hochladen"}
                <VisuallyHiddenInput
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={isCompressing}
                />
              </Button>
            </Grid>

            <Grid size={12}>
              <FromikTextField 
                formik={formik} 
                name="note" 
                label="Notiz" 
                placeholder="z.B. Es ist gut" 
                multiline
                rows={6}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ mt: 4, bgcolor: "#000000" }} />

        <Box mt={2} display="flex" justifyContent="flex-end">
          <CustomButton
            onNavigate={formik.handleSubmit}
            title="Speichern"
            disabled={loading || isCompressing}
          />
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default CreateReservation;